import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixEcomDiscountsV1DiscountRule from './ecom-discounts-v1-discount-rule-discount-rules.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface DiscountRule {
  /**
   * Discount rule ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the discount rule is updated.
   * To prevent conflicting changes, the current `revision` must be passed when updating the discount rule.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the discount rule was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the discount rule was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Whether the discount rule is active.
   *
   * Default: `true`
   */
  active?: boolean | null;
  /**
   * Discount rule name.
   * @minLength 1
   * @maxLength 50
   */
  name?: string | null;
  /**
   * Discount rule trigger.
   * A set of conditions that must be met for the `discounts` to be applied.
   * Not passing a trigger will cause the discount to always apply.
   */
  trigger?: DiscountTrigger;
  /** Time frame in which the discount rule is active. */
  activeTimeInfo?: ActiveTimeInfo;
  /**
   * List of discounts that are applied when one or more triggers are met.
   *
   * > **Notes:**
   * >
   * > + Currently, only 1 discount can be defined per discount rule.
   * > + A discount rule can be applied on multiple items per cart.
   * > + A discount rule can only be applied once per item.
   */
  discounts?: Discounts;
  /**
   * Discount rule status.
   * Default: `UNDEFINED`
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * Number of times the discount rule was used.
   * @readonly
   */
  usageCount?: number;
  /** Tags for this discount rule. */
  tags?: Tags;
}

/** DiscountTrigger - description of a set of conditions, that if met, will trigger the associated rule actions */
export interface DiscountTrigger extends DiscountTriggerTriggerOneOf {
  /** Chain multiple triggers with the `and` operator. */
  and?: And;
  /** Chain multiple triggers with the `or` operator. */
  or?: Or;
  /** Custom trigger. */
  customTrigger?: Custom;
  /** Subtotal trigger range. */
  subtotalRange?: SubtotalRange;
  /** Item quantity trigger range. */
  itemQuantityRange?: ItemQuantityRange;
  /** Customer Eligibility info */
  customerEligibility?: CustomerEligibility;
  /**
   * Trigger type.
   *
   * + `"AND"`: Operator used for chaining multiple triggers. Currently 1 `"AND"` chain operator is supported.
   * + `"SUBTOTAL_RANGE"`: Subtotal must be within the specified `subtotalRange` values.
   * + `"ITEM_QUANTITY_RANGE"`: Quantity of items in scope must be within specified `itemQuantityRange` values.
   * + `"CUSTOM"`: Custom trigger type defined in `customTrigger` object.
   */
  triggerType?: TriggerTypeWithLiterals;
}

/** @oneof */
export interface DiscountTriggerTriggerOneOf {
  /** Chain multiple triggers with the `and` operator. */
  and?: And;
  /** Chain multiple triggers with the `or` operator. */
  or?: Or;
  /** Custom trigger. */
  customTrigger?: Custom;
  /** Subtotal trigger range. */
  subtotalRange?: SubtotalRange;
  /** Item quantity trigger range. */
  itemQuantityRange?: ItemQuantityRange;
  /** Customer Eligibility info */
  customerEligibility?: CustomerEligibility;
}

/**
 * This object represents a scope of catalog items. Examples:
 * 1. All catalog items of a specific app - type = CATALOG_ITEM, CatalogItemFilter with `catalog_app_id`
 * 2. Specific catalog item - type = CATALOG_ITEM, CatalogItemFilter with `catalog_app_id` + `catalog_item_ids`
 * 3. External catalog filter - type = CUSTOM_FILTER, CustomFilter with 'app_id' + 'params'
 */
export interface Scope extends ScopeScopeItemsOneOf {
  /** Catalog item filter. Must be passed with `type."CATALOG_ITEM"`. */
  catalogItemFilter?: CatalogItemFilter;
  /** Custom filter. Must be passed with `type."CATALOG_ITEM"`. */
  customFilter?: CustomFilter;
  /**
   * Scope ID.
   * @minLength 1
   * @maxLength 100
   */
  _id?: string;
  /** Scope type. */
  type?: ScopeTypeWithLiterals;
}

/** @oneof */
export interface ScopeScopeItemsOneOf {
  /** Catalog item filter. Must be passed with `type."CATALOG_ITEM"`. */
  catalogItemFilter?: CatalogItemFilter;
  /** Custom filter. Must be passed with `type."CATALOG_ITEM"`. */
  customFilter?: CustomFilter;
}

export enum ScopeType {
  /** Specific catalog items */
  CATALOG_ITEM = 'CATALOG_ITEM',
  /** Specific items by custom filters */
  CUSTOM_FILTER = 'CUSTOM_FILTER',
}

/** @enumType */
export type ScopeTypeWithLiterals =
  | ScopeType
  | 'CATALOG_ITEM'
  | 'CUSTOM_FILTER';

export interface CatalogItemFilter {
  /**
   * Catalog App ID. For example, the Wix Stores, Wix Bookings, or 3rd-party [`appId`](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).
   * @format GUID
   */
  catalogAppId?: string;
  /**
   * ID of the item within its Wix or 3rd-party catalog. For example, `productId` for Wix Stores.
   * @minLength 1
   * @maxLength 36
   * @maxSize 50
   */
  catalogItemIds?: string[];
}

export interface CustomFilter {
  /**
   * Custom filter app ID, when relevant.
   * @format GUID
   */
  appId?: string;
  /**
   * Custom filter in `{ "key": "value" }` form.
   * For example, an array of `collectionIDs`:
   * `{ ["collectionId": "12345"], ["collectionId": "67890"] }`.
   */
  params?: Record<string, any> | null;
}

export interface And {
  /**
   * List of triggers to have an "AND" operator between their results.
   * @minSize 2
   * @maxSize 5
   */
  triggers?: DiscountTrigger[];
}

export interface Or {
  /**
   * List of triggers to have an "OR" operator between their results.
   * @minSize 2
   * @maxSize 5
   */
  triggers?: DiscountTrigger[];
}

export interface Custom {
  /**
   * Trigger ID.
   * @minLength 1
   * @maxLength 100
   */
  _id?: string;
  /**
   * ID of the app that created the trigger.
   * @format GUID
   */
  appId?: string;
}

export interface SubtotalRange {
  /**
   * Relevant scopes for `"SPECIFIC_ITEMS"` target type.
   * @maxSize 50
   */
  scopes?: Scope[];
  /**
   * Minimum subtotal price (inclusive).
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  from?: string | null;
  /**
   * Maximum subtotal price (inclusive).
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  to?: string | null;
}

export interface ItemQuantityRange {
  /**
   * Relevant scopes for `"SPECIFIC_ITEMS"` target type.
   * @maxSize 50
   */
  scopes?: Scope[];
  /**
   * Minimum item quantity (inclusive).
   * @min 1
   */
  from?: number | null;
  /**
   * Maximum item quantity (inclusive).
   * @min 1
   */
  to?: number | null;
}

export interface CustomerEligibility extends CustomerEligibilityOptionsOneOf {
  /** Information about specific members. */
  individualMembersInfo?: IndividualMembers;
  /** Customer eligibility type. */
  eligibilityType?: EligibilityTypeWithLiterals;
}

/** @oneof */
export interface CustomerEligibilityOptionsOneOf {
  /** Information about specific members. */
  individualMembersInfo?: IndividualMembers;
}

export enum EligibilityType {
  /** Only specific customers based on member id. */
  INDIVIDUAL_MEMBERS = 'INDIVIDUAL_MEMBERS',
}

/** @enumType */
export type EligibilityTypeWithLiterals =
  | EligibilityType
  | 'INDIVIDUAL_MEMBERS';

export interface ContactSegments {
  /**
   * Eligible segment ids.
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  segmentIds?: string[];
}

export interface ContactTags {
  /**
   * Eligible tag and label ids.
   * @minSize 1
   * @maxSize 100
   */
  tagReferences?: TagReference[];
}

export interface TagReference {
  /**
   * Eligible tag id.
   * @maxLength 5
   */
  tagId?: string;
  /**
   * Eligible label id.
   * @maxLength 80
   */
  labelKey?: string;
}

export interface IndividualMembers {
  /**
   * Eligible member ids.
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  memberIds?: string[];
}

export enum TriggerType {
  /** Operator used for chaining multiple triggers. Currently 1 `AND` chain operator is supported. */
  AND = 'AND',
  /** Subtotal must be within the specified `subtotalRange` values. */
  SUBTOTAL_RANGE = 'SUBTOTAL_RANGE',
  /** Quantity of items in scope must be within specified `itemQuantityRange` values. */
  ITEM_QUANTITY_RANGE = 'ITEM_QUANTITY_RANGE',
  /** Custom trigger type defined in `customTrigger` object. */
  CUSTOM = 'CUSTOM',
  /** Chain multiple triggers with OR operator */
  OR = 'OR',
  /**
   * Customer eligibility type must be within specified `customerEligibility` values.
   * @documentationMaturity preview
   */
  CUSTOMER_ELIGIBILITY = 'CUSTOMER_ELIGIBILITY',
}

/** @enumType */
export type TriggerTypeWithLiterals =
  | TriggerType
  | 'AND'
  | 'SUBTOTAL_RANGE'
  | 'ITEM_QUANTITY_RANGE'
  | 'CUSTOM'
  | 'OR'
  | 'CUSTOMER_ELIGIBILITY';

export interface ActiveTimeInfo {
  /** Date and time the discount rule is active **from**, in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format. */
  start?: Date | null;
  /** Date and time the discount rule is active **till**, in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format. */
  end?: Date | null;
}

export interface Recurrence {
  /** Weekly discount recurrence. */
  weeklySchedule?: WeeklySchedule;
}

export enum DayOfWeek {
  /** Monday. */
  MON = 'MON',
  /** Tuesday. */
  TUE = 'TUE',
  /** Wednesday. */
  WED = 'WED',
  /** Thursday. */
  THU = 'THU',
  /** Friday. */
  FRI = 'FRI',
  /** Saturday. */
  SAT = 'SAT',
  /** Sunday. */
  SUN = 'SUN',
}

/** @enumType */
export type DayOfWeekWithLiterals =
  | DayOfWeek
  | 'MON'
  | 'TUE'
  | 'WED'
  | 'THU'
  | 'FRI'
  | 'SAT'
  | 'SUN';

export interface TimeOfDay {
  /**
   * Hours. Min: `0`. Max: `23`.
   * @max 23
   */
  hours?: number;
  /**
   * Minutes. Min: `0`. Max: `59`.
   * @max 59
   */
  minutes?: number;
}

export interface TimeWindow {
  /** Day of week. */
  dayOfWeek?: DayOfWeekWithLiterals;
  /** Start time window during each recurrence. */
  startTime?: TimeOfDay;
  /** End time window during each recurrence. */
  endTime?: TimeOfDay;
}

export interface WeeklySchedule {
  /**
   * A list of days with their respective active time ranges.
   * @minSize 1
   * @maxSize 100
   */
  timeWindows?: TimeWindow[];
}

export interface Discounts {
  /**
   * Discounts.
   * @minSize 1
   * @maxSize 1
   */
  values?: Discount[];
}

export interface Discount extends DiscountDiscountOneOf {
  /**
   * Percentage to discount from original price.
   * @min 0.1
   * @max 100
   */
  percentage?: number;
  /**
   * Amount to discount from original price.
   * @decimalValue options { gt:0, lte:1000000000000000, maxScale:4 }
   */
  fixedAmount?: string;
  /**
   * Fixed price. Line item will be fixed to this price.
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  fixedPrice?: string;
  /** A special price is applied for a specific quantity of items. For example, 3 for $10. */
  quantityBased?: QuantityBased;
  /**
   * Discount target.
   *
   * + `"SPECIFIC_ITEMS"`: Discount applies to a specific set of items.
   */
  targetType?: TypeWithLiterals;
  /** Data related to `"SPECIFIC_ITEMS"` target type. */
  specificItemsInfo?: SpecificItemsInfo;
  /**
   * Discount type.
   *
   * + `"PERCENTAGE"`: Price is reduced by percentage value.
   * + `"FIXED_AMOUNT"`: Price is reduced by fixed amount.
   * + `"FIXED_PRICE"`: Price will be set to fixed amount.
   */
  discountType?: DiscountTypeWithLiterals;
}

/** @oneof */
export interface DiscountDiscountOneOf {
  /**
   * Percentage to discount from original price.
   * @min 0.1
   * @max 100
   */
  percentage?: number;
  /**
   * Amount to discount from original price.
   * @decimalValue options { gt:0, lte:1000000000000000, maxScale:4 }
   */
  fixedAmount?: string;
  /**
   * Fixed price. Line item will be fixed to this price.
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:4 }
   */
  fixedPrice?: string;
  /** A special price is applied for a specific quantity of items. For example, 3 for $10. */
  quantityBased?: QuantityBased;
}

export enum Type {
  /** Target type is a set of specific items */
  SPECIFIC_ITEMS = 'SPECIFIC_ITEMS',
}

/** @enumType */
export type TypeWithLiterals = Type | 'SPECIFIC_ITEMS';

export interface SpecificItemsInfo {
  /**
   * All associated scopes for `"SPECIFIC_ITEMS"` target type.
   * @minSize 1
   * @maxSize 50
   */
  scopes?: Scope[];
}

/** A special price is applied for a specific quantity of items. For example, 3 for $10. */
export interface QuantityBased {
  /**
   * list of discountConfig options.
   * @minSize 1
   * @maxSize 1
   */
  configurations?: DiscountConfig[];
}

export interface DiscountConfig {
  /**
   * DiscountConfig ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * The number of items that must be added to the cart to be eligible for the discount (Represents X).
   * @min 2
   */
  quantity?: number;
  /**
   * The DiscountConfig price (Represents Amount).
   * @decimalValue options { gte:0, lte:1000000, maxScale:4 }
   */
  price?: string;
}

export enum DiscountType {
  /** Price is reduced by percentage value. */
  PERCENTAGE = 'PERCENTAGE',
  /** Price is reduced by fixed amount. */
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  /** Price will be set to fixed amount. */
  FIXED_PRICE = 'FIXED_PRICE',
  /**
   * Quantity based options for Buy X For Amount.
   * @documentationMaturity preview
   */
  QUANTITY_BASED = 'QUANTITY_BASED',
}

/** @enumType */
export type DiscountTypeWithLiterals =
  | DiscountType
  | 'PERCENTAGE'
  | 'FIXED_AMOUNT'
  | 'FIXED_PRICE'
  | 'QUANTITY_BASED';

export interface BuyXGetYInfo {
  /** Information about which items must be in the cart (buy X) for the discount to apply (get Y). */
  customerBuys?: CustomerBuy;
  /** Information about which items will be discounted (get Y). */
  customerGets?: CustomerGet;
  /**
   * The maximum number of times the 'buy X get Y' discount can be applied.
   * For example, when the value of `limit` is `2`, with a "2+1" sale on all items, the following logic applies:
   * + Buy 2 get 1, buy 3 get 1.
   * + Buy 4 get 2, buy 6 get 2, buy 9 get 2, and so on.
   * @min 1
   */
  limit?: number | null;
}

export interface CustomerBuy extends CustomerBuyConditionOneOf {
  /**
   * Minimum number of items the customer must add to the cart to be eligible for a discount.
   * @min 1
   */
  minimumQuantity?: number;
  /**
   * Minimum price the customer must add to the cart to be eligible for a discount.
   * @decimalValue options { gte:0, lte:1000000000000000 }
   */
  minimumSpend?: string | null;
  /**
   * Scopes of the items that must be added to the cart to enable the discount.
   * @minSize 1
   * @maxSize 50
   */
  scopes?: Scope[];
}

/** @oneof */
export interface CustomerBuyConditionOneOf {
  /**
   * Minimum number of items the customer must add to the cart to be eligible for a discount.
   * @min 1
   */
  minimumQuantity?: number;
  /**
   * Minimum price the customer must add to the cart to be eligible for a discount.
   * @decimalValue options { gte:0, lte:1000000000000000 }
   */
  minimumSpend?: string | null;
}

export interface CustomerGet {
  /**
   * Exact number of items in the cart that will be discounted.
   * If the cart contains fewer items than the value of quantity, the discount will not apply.
   * @min 1
   */
  quantity?: number;
  /**
   * Scopes of the items that will be discounted.
   * @minSize 1
   * @maxSize 50
   */
  scopes?: Scope[];
}

export enum Status {
  /** Rule status is live. */
  LIVE = 'LIVE',
  /** Rule status is expired, it might have been live in the past. */
  EXPIRED = 'EXPIRED',
  /** Rule status is pending, it might be live in the future. */
  PENDING = 'PENDING',
}

/** @enumType */
export type StatusWithLiterals = Status | 'LIVE' | 'EXPIRED' | 'PENDING';

/** The discount settings */
export interface DiscountSettings {
  /** Discount applies to either `ALL_ITEMS`, or to the `LOWEST_PRICED_ITEM`. */
  appliesTo?: AppliedSubjectTypeWithLiterals;
  /**
   * Whether the discount will apply to subscriptions.
   *
   * Default: `false`
   */
  includeSubscription?: boolean | null;
  /**
   * Maximum total number of uses allowed for the discount rule.
   * @min 1
   * @max 2147483646
   */
  usageLimit?: number | null;
  /**
   * The usage limit per user per discount.
   * When not provided, this setting will not apply.
   * @min 1
   * @max 2147483646
   */
  usageLimitPerUser?: number | null;
}

/** TODO: check if can be removed */
export enum AppliedSubjectType {
  /** Discount applies to all items at checkout. */
  ALL_ITEMS = 'ALL_ITEMS',
  /** Discount applies to the lowest priced item at checkout. */
  LOWEST_PRICED_ITEM = 'LOWEST_PRICED_ITEM',
}

/** @enumType */
export type AppliedSubjectTypeWithLiterals =
  | AppliedSubjectType
  | 'ALL_ITEMS'
  | 'LOWEST_PRICED_ITEM';

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * option (.wix.api.decomposite_of) = "wix.commons.v2.tags.Foo";
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * public_tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  publicTags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs.
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface TagsModified {
  /** The discount rule after tags were modified. */
  discountRule?: DiscountRule;
  /** Tags that were assigned. */
  assignedTags?: Tags;
  /** Tags that were unassigned. */
  unassignedTags?: Tags;
}

export interface CreateDiscountRuleRequest {
  /** Discount rule info. */
  discountRule: DiscountRule;
}

export interface CreateDiscountRuleResponse {
  /** Discount rule. */
  discountRule?: DiscountRule;
}

export interface GetDiscountRuleRequest {
  /**
   * ID of the discount rule to retrieve.
   * @format GUID
   */
  discountRuleId: string;
}

export interface GetDiscountRuleResponse {
  /** The requested discount rule. */
  discountRule?: DiscountRule;
}

export interface UpdateDiscountRuleRequest {
  /** Discount rule info. */
  discountRule: DiscountRule;
}

export interface UpdateDiscountRuleResponse {
  /** Updated discount rule. */
  discountRule?: DiscountRule;
}

export interface DeleteDiscountRuleRequest {
  /**
   * ID of the discount rule to delete.
   * @format GUID
   */
  discountRuleId: string;
}

export interface DeleteDiscountRuleResponse {}

export interface QueryDiscountRulesRequest {
  /** Query options. */
  query?: PlatformQuery;
}

export interface PlatformQuery extends PlatformQueryPagingMethodOneOf {
  /** Pointer to page of results using offset. Cannot be used together with `cursorPaging`. */
  paging?: PlatformPaging;
  /** Cursor pointing to page of results. Cannot be used together with `paging`. `cursorPaging.cursor` can not be used together with `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /** Filter object. */
  filter?: Record<string, any> | null;
  /** Sorting options. For example, `[{"fieldName":"sortField1"},{"fieldName":"sortField2","direction":"DESC"}]`. */
  sort?: Sorting[];
}

/** @oneof */
export interface PlatformQueryPagingMethodOneOf {
  /** Pointer to page of results using offset. Cannot be used together with `cursorPaging`. */
  paging?: PlatformPaging;
  /** Cursor pointing to page of results. Cannot be used together with `paging`. `cursorPaging.cursor` can not be used together with `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface PlatformPaging {
  /**
   * Number of items to load.
   * @max 100
   */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryDiscountRulesResponse {
  /** List of discount rules. */
  discountRules?: DiscountRule[];
  /** Details on the paged set of results returned. */
  pagingMetadata?: PlatformPagingMetadata;
}

export interface PlatformPagingMetadata {
  /** The number of items returned in this response. */
  count?: number | null;
  /** The offset which was requested. Returned if offset paging was used. */
  offset?: number | null;
  /** The total number of items that match the query. Returned if offset paging was used. */
  total?: number | null;
  /** Cursors to navigate through result pages. Returned if cursor paging was used. */
  cursors?: Cursors;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface GetAppliedDiscountsRequest {
  /**
   * Line items for which to check for discount rules.
   * @maxSize 1000
   */
  lineItems?: LineItem[];
  /**
   * Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.
   * @format GUID
   */
  purchaseFlowId?: string | null;
  /** Buyer Information */
  buyerInfo?: BuyerInfo;
  /** Shipping information. */
  shippingInfo?: ShippingInfo;
  /**
   * The business location ID to be used as the basis for some calculations.
   * Learn more about the [Locations API](https://dev.wix.com/docs/rest/business-management/locations/introduction).
   * @format GUID
   */
  businessLocationId?: string | null;
}

export interface LineItem {
  /**
   * Line item ID.
   * @minLength 1
   * @maxLength 100
   */
  _id?: string;
  /**
   * Line item quantity.
   * @max 100000
   */
  quantity?: number | null;
  /** Catalog and item reference info. Learn more about [integrating Wix Stores products with Wix eCommerce](https://dev.wix.com/api/rest/wix-stores/catalog/ecommerce-integration). */
  catalogReference?: CatalogReference;
  /**
   * Line item price.
   * @decimalValue options { gte:0 }
   */
  price?: string;
  /**
   * Additional ID for the item in the catalog.
   * This field provides an additional identifier for the item if your flow requires another ID as well as `catalogReference.catalogItemId`.
   * Can be used for membership validation.
   * @minLength 1
   * @maxLength 36
   */
  rootCatalogItemId?: string | null;
}

/** Used for grouping line items. Sent when an item is added to a cart, checkout, or order. */
export interface CatalogReference {
  /**
   * ID of the item within the catalog it belongs to.
   * @minLength 1
   * @maxLength 36
   */
  catalogItemId?: string;
  /**
   * ID of the app providing the catalog.
   *
   * You can get your app's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).
   *
   * For items from Wix catalogs, the following values always apply:
   * + Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`
   * + Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`
   * + Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`
   * @minLength 1
   */
  appId?: string;
  /**
   * Additional item details in `key:value` pairs.
   *
   * Use this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.
   *
   * For Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)
   * or [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).
   */
  options?: Record<string, any> | null;
}

export interface BuyerInfo {
  /**
   * Email of the customer.
   * @format EMAIL
   */
  buyerEmail?: string | null;
  /**
   * Member id of the customer.
   * @format GUID
   */
  memberId?: string | null;
}

export interface ShippingInfo {
  /** Address (if applicable). */
  address?: Address;
  /**
   * Preferred shipping option code. For example, "usps_std_overnight".
   * @minLength 1
   * @maxLength 100
   */
  preferredShippingOptionCode?: string | null;
}

export interface Address {
  /**
   * Two-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.
   * @format COUNTRY
   */
  country?: string | null;
  /**
   * Code for a subdivision (such as state, prefecture, or province) in [ISO 3166-2](https://www.iso.org/standard/72483.html) format.
   * @maxLength 50
   */
  subdivision?: string | null;
  /**
   * City name.
   * @maxLength 50
   */
  city?: string | null;
}

export interface GetAppliedDiscountsResponse {
  /** All eligible discounts. */
  appliedDiscounts?: AppliedDiscount[];
}

export interface AppliedDiscount {
  /** Discount type. */
  discountType?: TypeWithLiterals;
  /**
   * IDs of line items the discount applies to, in GUID format.
   * @format GUID
   * @maxSize 1000
   */
  lineItemIds?: string[];
  /** Applied discount rule. */
  appliedDiscountRule?: AppliedDiscountRule;
  /**
   * A list of bundle participation for this specific line item.
   * Each entry represents a unique bundle composition that this line item
   * participated in, along with how many times that bundle composition was formed by the discount rule.
   * A single line item may appear in *multiple* item_combination
   * Relevant ONLY for:
   * - Buy X Get Y (BXGY) discounts
   * - Quantity-based discounts
   * @internal
   * @maxSize 100
   */
  itemCombinations?: ItemCombination[];
}

export interface AppliedDiscountRule {
  /**
   * Applied discount rule ID.
   * @format GUID
   */
  _id?: string;
  /** Discount rule name. */
  name?: DiscountRuleName;
  /** Total amount reduced from all discounted line items. */
  amount?: MultiCurrencyPrice;
  /** Discount rule type. */
  discountRuleType?: DiscountTypeWithLiterals;
}

export interface DiscountRuleName {
  /**
   * Original discount rule name (in site's default language).
   * @minLength 1
   * @maxLength 256
   */
  original?: string;
  /**
   * Translated discount rule name according to buyer language.
   *
   * Default: `original`
   * @minLength 1
   * @maxLength 500
   */
  translated?: string | null;
}

export interface MultiCurrencyPrice {
  /**
   * Amount.
   * @decimalValue options { gte:0, lte:1000000000000000 }
   */
  amount?: string;
  /**
   * Converted amount.
   * @readonly
   * @decimalValue options { gte:0, lte:1000000000000000 }
   */
  convertedAmount?: string;
  /**
   * Amount formatted with currency symbol.
   * @readonly
   */
  formattedAmount?: string;
  /**
   * Converted amount formatted with currency symbol.
   * @readonly
   */
  formattedConvertedAmount?: string;
}

export interface ItemCombination {
  /**
   * Number of times this specific bundle composition was formed
   * by the discount rule.
   */
  repeatCount?: number;
  /**
   * Line items that participated together in this bundle composition.
   * @maxSize 100
   */
  lineItems?: ItemCombinationLineItem[];
}

export interface ItemCombinationLineItem {
  /**
   * Line item ID that took part in this bundle composition.
   * @format GUID
   */
  lineItemId?: string;
  /** Discount amount applied to this line item *per bundle composition*. */
  amount?: MultiCurrencyPrice;
  /**
   * Number of units from this line item that participated
   * in a single bundle composition.
   * @min 1
   * @max 1000000
   */
  discountedQuantity?: number;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface DiscountRuleUsageLimitReached {
  /** Discount Rule */
  rule?: DiscountRule;
}

export interface BulkUpdateDiscountRuleTagsRequest {
  /**
   * IDs of discount rules to update.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  discountRuleIds: string[];
  /** Tags to assign to the discount rules. */
  assignTags?: Tags;
  /** Tags to unassign from the discount rules. */
  unassignTags?: Tags;
}

export interface BulkUpdateDiscountRuleTagsResponse {
  /**
   * Results
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateDiscountRuleTagsResult[];
  /** Metadata regarding the bulk update operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed.
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action for this item was successful. When `false`, the `error` field is returned. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkUpdateDiscountRuleTagsResult {
  /** Metadata regarding the specific single update operation */
  itemMetadata?: ItemMetadata;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateDiscountRuleTagsByFilterRequest {
  /** Filter. */
  filter: Record<string, any> | null;
  /** Tags to assign to the discount rules. */
  assignTags?: Tags;
  /** Tags to unassign from the discount rules. */
  unassignTags?: Tags;
}

export interface BulkUpdateDiscountRuleTagsByFilterResponse {
  /**
   * Job ID.
   * @format GUID
   */
  jobId?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateDiscountRuleApplicationErrors =
  | {
      code?: 'MAX_DISCOUNT_RULES_REACHED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MAX_QUANTITY_BASED_DISCOUNTS_REACHED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MAX_CUSTOM_TRIGGER_APPS_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type CreateDiscountRuleValidationErrors =
  | {
      ruleName?: 'INVALID_ACTIVE_TIME_RANGE';
    }
  | {
      ruleName?: 'INVALID_EMPTY_DISCOUNT';
    }
  | {
      ruleName?: 'INVALID_DISCOUNT_TYPE';
    }
  | {
      ruleName?: 'MISSING_SUBTOTAL_RANGE';
    }
  | {
      ruleName?: 'MISSING_ITEM_QUANTITY_RANGE';
    }
  | {
      ruleName?: 'INVALID_SUBTOTAL_RANGE';
    }
  | {
      ruleName?: 'INVALID_ITEM_QUANTITY_RANGE';
    }
  | {
      ruleName?: 'INVALID_TARGET_TYPE_FIELD';
    }
  | {
      ruleName?: 'OPERATOR_TOO_DEEPLY_NESTED';
    }
  | {
      ruleName?: 'INVALID_SCOPE_TYPE';
    }
  | {
      ruleName?: 'INVALID_TRIGGER_TYPE';
    }
  | {
      ruleName?: 'INVALID_SETTINGS_SUBJECT_TYPE';
    }
  | {
      ruleName?: 'INVALID_CUSTOMER_BUY_CONDITION';
    }
  | {
      ruleName?: 'INVALID_SCOPE';
    };
/** @docsIgnore */
export type BulkUpdateDiscountRuleTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateDiscountRuleTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface DiscountRuleCreatedEnvelope {
  entity: DiscountRule;
  metadata: EventMetadata;
}

/**
 * Triggered when a discount rule is created.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read eCommerce - all read permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.READ-ECOM
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionId ECOM.DISCOUNT_RULES_READ
 * @webhook
 * @eventType wix.ecom.discounts.v1.discount_rule_created
 * @serviceIdentifier com.wix.ecom.discounts.DiscountRuleService
 * @slug created
 */
export declare function onDiscountRuleCreated(
  handler: (event: DiscountRuleCreatedEnvelope) => void | Promise<void>
): void;

export interface DiscountRuleDeletedEnvelope {
  metadata: EventMetadata;
}

/**
 * Triggered when a discount rule is deleted.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read eCommerce - all read permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.READ-ECOM
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionId ECOM.DISCOUNT_RULES_READ
 * @webhook
 * @eventType wix.ecom.discounts.v1.discount_rule_deleted
 * @serviceIdentifier com.wix.ecom.discounts.DiscountRuleService
 * @slug deleted
 */
export declare function onDiscountRuleDeleted(
  handler: (event: DiscountRuleDeletedEnvelope) => void | Promise<void>
): void;

export interface DiscountRuleUpdatedEnvelope {
  entity: DiscountRule;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when a discount rule is updated.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read eCommerce - all read permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.READ-ECOM
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionId ECOM.DISCOUNT_RULES_READ
 * @webhook
 * @eventType wix.ecom.discounts.v1.discount_rule_updated
 * @serviceIdentifier com.wix.ecom.discounts.DiscountRuleService
 * @slug updated
 */
export declare function onDiscountRuleUpdated(
  handler: (event: DiscountRuleUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a new discount rule.
 *
 *
 * The `createDiscountRule()` function returns a Promise that resolves to the new discount rule when it's created.
 * @param discountRule - Discount rule info.
 * @public
 * @requiredField discountRule
 * @requiredField discountRule.discounts
 * @requiredField discountRule.name
 * @permissionId ECOM.DISCOUNT_RULES_CREATE
 * @applicableIdentity APP
 * @returns Discount rule.
 * @fqn com.wix.ecom.discounts.DiscountRuleService.CreateDiscountRule
 */
export async function createDiscountRule(
  discountRule: NonNullablePaths<DiscountRule, `discounts` | `name`, 2>
): Promise<
  NonNullablePaths<
    DiscountRule,
    | `trigger.and.triggers`
    | `trigger.or.triggers`
    | `trigger.customTrigger._id`
    | `trigger.customTrigger.appId`
    | `trigger.subtotalRange.scopes`
    | `trigger.subtotalRange.scopes.${number}.catalogItemFilter.catalogAppId`
    | `trigger.subtotalRange.scopes.${number}.customFilter.appId`
    | `trigger.subtotalRange.scopes.${number}._id`
    | `trigger.subtotalRange.scopes.${number}.type`
    | `trigger.itemQuantityRange.scopes`
    | `trigger.customerEligibility.individualMembersInfo.memberIds`
    | `trigger.customerEligibility.eligibilityType`
    | `trigger.triggerType`
    | `discounts.values`
    | `discounts.values.${number}.percentage`
    | `discounts.values.${number}.fixedAmount`
    | `discounts.values.${number}.fixedPrice`
    | `discounts.values.${number}.targetType`
    | `discounts.values.${number}.discountType`
    | `status`
    | `usageCount`
    | `tags.privateTags.tagIds`,
    7
  > & {
    __applicationErrorsType?: CreateDiscountRuleApplicationErrors;
    __validationErrorsType?: CreateDiscountRuleValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    discountRule: discountRule,
  });

  const reqOpts =
    ambassadorWixEcomDiscountsV1DiscountRule.createDiscountRule(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.discountRule!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { discountRule: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['discountRule']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a discount rule.
 *
 *
 * The `getDiscountRule()` function returns a Promise that resolves when the specified discount rule is retrieved.
 * @param discountRuleId - ID of the discount rule to retrieve.
 * @public
 * @requiredField discountRuleId
 * @permissionId ECOM.DISCOUNT_RULES_READ
 * @applicableIdentity APP
 * @returns The requested discount rule.
 * @fqn com.wix.ecom.discounts.DiscountRuleService.GetDiscountRule
 */
export async function getDiscountRule(
  discountRuleId: string
): Promise<
  NonNullablePaths<
    DiscountRule,
    | `trigger.and.triggers`
    | `trigger.or.triggers`
    | `trigger.customTrigger._id`
    | `trigger.customTrigger.appId`
    | `trigger.subtotalRange.scopes`
    | `trigger.subtotalRange.scopes.${number}.catalogItemFilter.catalogAppId`
    | `trigger.subtotalRange.scopes.${number}.customFilter.appId`
    | `trigger.subtotalRange.scopes.${number}._id`
    | `trigger.subtotalRange.scopes.${number}.type`
    | `trigger.itemQuantityRange.scopes`
    | `trigger.customerEligibility.individualMembersInfo.memberIds`
    | `trigger.customerEligibility.eligibilityType`
    | `trigger.triggerType`
    | `discounts.values`
    | `discounts.values.${number}.percentage`
    | `discounts.values.${number}.fixedAmount`
    | `discounts.values.${number}.fixedPrice`
    | `discounts.values.${number}.targetType`
    | `discounts.values.${number}.discountType`
    | `status`
    | `usageCount`
    | `tags.privateTags.tagIds`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    discountRuleId: discountRuleId,
  });

  const reqOpts =
    ambassadorWixEcomDiscountsV1DiscountRule.getDiscountRule(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.discountRule!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { discountRuleId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['discountRuleId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a discount rule's properties.
 *
 *
 * The `updateDiscountRule()` function returns a Promise that resolves when the specified discount rule's properties are updated.
 *
 * Each time the discount rule is updated, `revision` increments by 1. The existing `revision` must be included when updating the discount rule. This ensures you're working with the latest discount rule information, and it prevents unintended overwrites.
 * @param _id - Discount rule ID.
 * @public
 * @requiredField _id
 * @requiredField discountRule
 * @requiredField discountRule.revision
 * @param discountRule - Discount rule info.
 * @param options - Discount rule info.
 * @permissionId ECOM.DISCOUNT_RULES_UPDATE
 * @applicableIdentity APP
 * @returns Updated discount rule.
 * @fqn com.wix.ecom.discounts.DiscountRuleService.UpdateDiscountRule
 */
export async function updateDiscountRule(
  _id: string,
  discountRule: NonNullablePaths<UpdateDiscountRule, `revision`, 2>
): Promise<
  NonNullablePaths<
    DiscountRule,
    | `trigger.and.triggers`
    | `trigger.or.triggers`
    | `trigger.customTrigger._id`
    | `trigger.customTrigger.appId`
    | `trigger.subtotalRange.scopes`
    | `trigger.subtotalRange.scopes.${number}.catalogItemFilter.catalogAppId`
    | `trigger.subtotalRange.scopes.${number}.customFilter.appId`
    | `trigger.subtotalRange.scopes.${number}._id`
    | `trigger.subtotalRange.scopes.${number}.type`
    | `trigger.itemQuantityRange.scopes`
    | `trigger.customerEligibility.individualMembersInfo.memberIds`
    | `trigger.customerEligibility.eligibilityType`
    | `trigger.triggerType`
    | `discounts.values`
    | `discounts.values.${number}.percentage`
    | `discounts.values.${number}.fixedAmount`
    | `discounts.values.${number}.fixedPrice`
    | `discounts.values.${number}.targetType`
    | `discounts.values.${number}.discountType`
    | `status`
    | `usageCount`
    | `tags.privateTags.tagIds`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    discountRule: { ...discountRule, id: _id },
  });

  const reqOpts =
    ambassadorWixEcomDiscountsV1DiscountRule.updateDiscountRule(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.discountRule!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { discountRule: '$[1]' },
        explicitPathsToArguments: { 'discountRule.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'discountRule']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateDiscountRule {
  /**
   * Discount rule ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the discount rule is updated.
   * To prevent conflicting changes, the current `revision` must be passed when updating the discount rule.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the discount rule was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the discount rule was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Whether the discount rule is active.
   *
   * Default: `true`
   */
  active?: boolean | null;
  /**
   * Discount rule name.
   * @minLength 1
   * @maxLength 50
   */
  name?: string | null;
  /**
   * Discount rule trigger.
   * A set of conditions that must be met for the `discounts` to be applied.
   * Not passing a trigger will cause the discount to always apply.
   */
  trigger?: DiscountTrigger;
  /** Time frame in which the discount rule is active. */
  activeTimeInfo?: ActiveTimeInfo;
  /**
   * List of discounts that are applied when one or more triggers are met.
   *
   * > **Notes:**
   * >
   * > + Currently, only 1 discount can be defined per discount rule.
   * > + A discount rule can be applied on multiple items per cart.
   * > + A discount rule can only be applied once per item.
   */
  discounts?: Discounts;
  /**
   * Discount rule status.
   * Default: `UNDEFINED`
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * Number of times the discount rule was used.
   * @readonly
   */
  usageCount?: number;
  /** Tags for this discount rule. */
  tags?: Tags;
}

/**
 * Deletes a discount rule.
 *
 *
 * The `deleteDiscountRule()` function returns a Promise that resolves when the specified discount rule is deleted.
 * @param discountRuleId - ID of the discount rule to delete.
 * @public
 * @requiredField discountRuleId
 * @permissionId ECOM.DISCOUNT_RULES_DELETE
 * @applicableIdentity APP
 * @fqn com.wix.ecom.discounts.DiscountRuleService.DeleteDiscountRule
 */
export async function deleteDiscountRule(
  discountRuleId: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    discountRuleId: discountRuleId,
  });

  const reqOpts =
    ambassadorWixEcomDiscountsV1DiscountRule.deleteDiscountRule(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { discountRuleId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['discountRuleId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a query to retrieve a list of discount rules.
 *
 *
 * The `queryDiscountRules()` function builds a query to retrieve a list of up to 100 discount rules, and returns a [`DiscountRulesQueryBuilder`](#discountrulesquerybuilder) object.
 *
 * The returned object contains the query definition which is typically used to run the query using the [`find()`](/discount-rules/discount-rules-query-builder/find) function.
 *
 * You can refine the query by chaining `DiscountRulesQueryBuilder` functions onto the query. `DiscountRulesQueryBuilder` functions enable you to sort, filter, and control the results queryDiscountRules() returns.
 *
 * By default, `queryDiscountRules()` sorts results by [`ascending("_id")`](/discount-rules/discount-rules-query-builder/ascending) by default. This can be overridden.
 *
 * To learn how to query posts, refer to the table below.
 *
 * The following `DiscountRulesQueryBuilder` functions are supported for the `queryDiscountRules()` function. For a full description of the discount rule object, see the object returned for the [`items`](/discount-rules/discount-rules-query-result/items) property in the `DiscountRulesQueryResult`.
 * @public
 * @permissionId ECOM.DISCOUNT_RULES_READ
 * @applicableIdentity APP
 * @fqn com.wix.ecom.discounts.DiscountRuleService.QueryDiscountRules
 */
export function queryDiscountRules(): DiscountRulesQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    DiscountRule,
    'CURSOR',
    QueryDiscountRulesRequest,
    QueryDiscountRulesResponse
  >({
    func: async (payload: QueryDiscountRulesRequest) => {
      const reqOpts =
        ambassadorWixEcomDiscountsV1DiscountRule.queryDiscountRules(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryDiscountRulesRequest['query']) => {
      const args = [query, {}] as [QueryDiscountRulesRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryDiscountRulesResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.discountRules,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface DiscountRulesQueryResult extends QueryCursorResult {
  items: DiscountRule[];
  query: DiscountRulesQueryBuilder;
  next: () => Promise<DiscountRulesQueryResult>;
  prev: () => Promise<DiscountRulesQueryResult>;
}

export interface DiscountRulesQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | '_id'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'active'
      | 'name'
      | 'activeTimeInfo.start'
      | 'activeTimeInfo.end'
      | 'tags.privateTags.tagIds',
    value: any
  ) => DiscountRulesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | '_id'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'active'
      | 'name'
      | 'activeTimeInfo.start'
      | 'activeTimeInfo.end'
      | 'tags.privateTags.tagIds',
    value: any
  ) => DiscountRulesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | '_id'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'activeTimeInfo.start'
      | 'activeTimeInfo.end',
    value: any
  ) => DiscountRulesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | '_id'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'activeTimeInfo.start'
      | 'activeTimeInfo.end',
    value: any
  ) => DiscountRulesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | '_id'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'activeTimeInfo.start'
      | 'activeTimeInfo.end',
    value: any
  ) => DiscountRulesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | '_id'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'activeTimeInfo.start'
      | 'activeTimeInfo.end',
    value: any
  ) => DiscountRulesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: '_id' | 'name',
    value: string
  ) => DiscountRulesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName:
      | '_id'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'active'
      | 'name'
      | 'activeTimeInfo.start'
      | 'activeTimeInfo.end'
      | 'tags.privateTags.tagIds',
    value: any[]
  ) => DiscountRulesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasAll: (
    propertyName: 'tags.privateTags.tagIds',
    value: any[]
  ) => DiscountRulesQueryBuilder;
  in: (
    propertyName:
      | '_id'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'active'
      | 'name'
      | 'activeTimeInfo.start'
      | 'activeTimeInfo.end'
      | 'tags.privateTags.tagIds',
    value: any
  ) => DiscountRulesQueryBuilder;
  exists: (
    propertyName:
      | '_id'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'active'
      | 'name'
      | 'activeTimeInfo.start'
      | 'activeTimeInfo.end'
      | 'tags.privateTags.tagIds',
    value: boolean
  ) => DiscountRulesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'active'
      | 'name'
      | 'activeTimeInfo.start'
      | 'activeTimeInfo.end'
      | 'tags.privateTags.tagIds'
    >
  ) => DiscountRulesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | 'revision'
      | '_createdDate'
      | '_updatedDate'
      | 'active'
      | 'name'
      | 'activeTimeInfo.start'
      | 'activeTimeInfo.end'
      | 'tags.privateTags.tagIds'
    >
  ) => DiscountRulesQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => DiscountRulesQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => DiscountRulesQueryBuilder;
  find: () => Promise<DiscountRulesQueryResult>;
}

/**
 * @hidden
 * @fqn com.wix.ecom.discounts.DiscountRuleService.QueryDiscountRules
 * @requiredField query
 */
export async function typedQueryDiscountRules(
  query: DiscountRuleQuery
): Promise<
  NonNullablePaths<
    QueryDiscountRulesResponse,
    | `discountRules`
    | `discountRules.${number}.trigger.customTrigger._id`
    | `discountRules.${number}.trigger.customTrigger.appId`
    | `discountRules.${number}.trigger.customerEligibility.eligibilityType`
    | `discountRules.${number}.trigger.triggerType`
    | `discountRules.${number}.status`
    | `discountRules.${number}.usageCount`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixEcomDiscountsV1DiscountRule.queryDiscountRules(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DiscountRuleQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['tags.privateTags.tagIds'];
      operators: ['$hasAll', '$hasSome'];
      sort: 'BOTH';
    },
    {
      fields: [
        '_createdDate',
        '_id',
        '_updatedDate',
        'active',
        'activeTimeInfo.end',
        'activeTimeInfo.start',
        'name',
        'revision'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  DiscountRule,
  DiscountRuleQuerySpec
>;
export type DiscountRuleQuery = {
  /** 
  Cursor pointing to page of results. Cannot be used together with `paging`. `cursorPaging.cursor` can not be used together with `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sorting options. For example, `[{"fieldName":"sortField1"},{"fieldName":"sortField2","direction":"DESC"}]`.  
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<
      DiscountRule,
      DiscountRuleQuerySpec,
      DiscountRuleQuery
    >(),
  },
};

/**
 * Bulk update tags for discount rules by IDs.
 * @param discountRuleIds - IDs of discount rules to update.
 * @public
 * @requiredField discountRuleIds
 * @permissionId ECOM.DISCOUNT_RULES_UPDATE_TAGS
 * @applicableIdentity APP
 * @fqn com.wix.ecom.discounts.DiscountRuleService.BulkUpdateDiscountRuleTags
 */
export async function bulkUpdateDiscountRuleTags(
  discountRuleIds: string[],
  options?: BulkUpdateDiscountRuleTagsOptions
): Promise<
  NonNullablePaths<
    BulkUpdateDiscountRuleTagsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkUpdateDiscountRuleTagsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    discountRuleIds: discountRuleIds,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixEcomDiscountsV1DiscountRule.bulkUpdateDiscountRuleTags(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          discountRuleIds: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['discountRuleIds', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateDiscountRuleTagsOptions {
  /** Tags to assign to the discount rules. */
  assignTags?: Tags;
  /** Tags to unassign from the discount rules. */
  unassignTags?: Tags;
}

/**
 * Asynchronously update tags on multiple discount rules by filter.
 * An empty filter will update all discount rules.
 * A tag that appears both in the list of assign and unassign tags, will be assigned.
 * @param filter - Filter.
 * @public
 * @requiredField filter
 * @permissionId ECOM.DISCOUNT_RULES_UPDATE_TAGS
 * @applicableIdentity APP
 * @fqn com.wix.ecom.discounts.DiscountRuleService.BulkUpdateDiscountRuleTagsByFilter
 */
export async function bulkUpdateDiscountRuleTagsByFilter(
  filter: Record<string, any>,
  options?: BulkUpdateDiscountRuleTagsByFilterOptions
): Promise<
  NonNullablePaths<BulkUpdateDiscountRuleTagsByFilterResponse, `jobId`, 2> & {
    __applicationErrorsType?: BulkUpdateDiscountRuleTagsByFilterApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixEcomDiscountsV1DiscountRule.bulkUpdateDiscountRuleTagsByFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateDiscountRuleTagsByFilterOptions {
  /** Tags to assign to the discount rules. */
  assignTags?: Tags;
  /** Tags to unassign from the discount rules. */
  unassignTags?: Tags;
}
